import { useMemo } from "react";
import { format, parseISO } from "date-fns";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  TooltipProps,
} from "recharts";
import { DateRange } from "react-day-picker";
import { useNewCustomersDaily, NewCustomerData } from "@/api/analytics";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";

interface Props {
  dateRange: DateRange;
}

interface ChartDataItem {
  date: string;
  formattedDate: string;
  count: number;
}

// Transform data for the chart
const transformData = (
  data: NewCustomerData[] | undefined | null
): ChartDataItem[] => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return [];
  }

  return data
    .map((item) => ({
      date: item.date,
      formattedDate: format(parseISO(item.date), "MMM d"),
      count: item.count,
    }))
    .sort((a, b) => a.date.localeCompare(b.date));
};

// Custom tooltip component
const CustomTooltip = ({
  active,
  payload,
  label,
}: TooltipProps<number, string>) => {
  if (active && payload?.length) {
    return (
      <Card className="p-2 bg-white shadow-md border">
        <CardContent className="p-2">
          <p className="font-bold">{label}</p>
          <div className="flex justify-between">
            <span className="text-[#10b981]">New Customers: </span>
            <span className="font-semibold ml-2">{payload[0]?.value}</span>
          </div>
        </CardContent>
      </Card>
    );
  }
  return null;
};

export default function NewCustomersDaily({ dateRange }: Props) {
  // Use the API hook
  const { data, isLoading, error } = useNewCustomersDaily(dateRange);

  // Calculate total new customers in the period
  const totalNewCustomers = useMemo(() => {
    if (!data || !Array.isArray(data)) return 0;
    return data.reduce((sum, item) => sum + item.count, 0);
  }, [data]);

  // Transform data for chart
  const chartData = useMemo(() => transformData(data), [data]);

  if (isLoading) {
    return (
      <div className="w-full h-[300px] flex items-center justify-center">
        <Skeleton className="w-full h-[300px]" />
      </div>
    );
  }

  if (error) {
    console.error("Error fetching new customers data:", error);
    return (
      <div className="w-full h-[300px] flex items-center justify-center text-red-500">
        Failed to fetch data. Please try again.
      </div>
    );
  }

  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <div className="w-full h-[300px] flex items-center justify-center text-gray-500">
        No data available for the selected date range
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center p-3 bg-muted rounded">
        <span className="font-medium">Total New Customers</span>
        <span className="text-lg font-bold">{totalNewCustomers}</span>
      </div>

      <div className="w-full h-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 20, right: 30, left: 20, bottom: 10 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="formattedDate" />
            <YAxis />
            <Tooltip content={<CustomTooltip />} />
            <Bar
              dataKey="count"
              fill="#10b981"
              name="New Customers"
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
